import { useEffect, useMemo, useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { computePeaks } from "@/components/daw/MicroWaveform";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/**
 * Big Play button + clickable waveform driving the SHARED engine transport
 * (same audio the Pro Studio will see — no second audio element).
 */
export default function CreatorPlayer({ engine, channel, loadState, onToggle }) {
  const buffer = channel != null ? engine?.channels?.[channel]?.buffer || null : null;
  const peaks = useMemo(() => computePeaks(buffer, 96), [buffer]);
  const dur = buffer?.duration || 0;
  const [playing, setPlaying] = useState(!!engine?.playing);
  const [pos, setPos] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setPlaying(!!engine?.playing);
      setPos(engine?.getPlayhead?.() || 0);
    }, 200);
    return () => clearInterval(id);
  }, [engine]);

  const toggle = async () => {
    if (!buffer) return;
    if (engine.playing) {
      engine.pause();
      setPlaying(false);
    } else {
      if (engine.ctx?.state === "suspended") await engine.ctx.resume();
      if (engine.getPlayhead() >= dur - 0.05) engine.seekTo(0);
      await engine.play();
      setPlaying(true);
    }
    onToggle?.(!!engine.playing);
  };

  const seek = (e) => {
    if (!dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    engine.seekTo(frac * dur);
    setPos(frac * dur);
  };

  const shown = Math.min(pos, dur);
  const pct = dur > 0 ? (shown / dur) * 100 : 0;
  const loading = loadState === "loading" || (loadState !== "error" && !buffer);

  return (
    <div data-testid="creator-player" data-playing={playing ? "1" : "0"} className="flex items-center gap-4 rounded-3xl border border-white/[0.08] bg-black/30 p-4 sm:p-5">
      <button
        type="button"
        data-testid="creator-play-btn"
        data-playing={playing ? "true" : "false"}
        onClick={toggle}
        disabled={!buffer}
        aria-label={playing ? "Pause" : "Play"}
        className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-[0_10px_34px_rgba(99,102,241,0.5)] transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
      >
        {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : playing ? <Pause className="h-7 w-7" /> : <Play className="ml-1 h-7 w-7" />}
      </button>
      <div className="min-w-0 flex-1">
        <div
          role="slider"
          aria-label="Song position"
          aria-valuemin={0}
          aria-valuemax={Math.round(dur)}
          aria-valuenow={Math.round(shown)}
          tabIndex={0}
          data-testid="creator-waveform"
          onClick={seek}
          onKeyDown={(e) => { if (e.key === "ArrowRight") engine.seekTo(shown + 5); if (e.key === "ArrowLeft") engine.seekTo(Math.max(0, shown - 5)); }}
          className="relative flex h-16 cursor-pointer items-center gap-[2px] overflow-hidden rounded-xl"
        >
          {peaks.length ? peaks.map((p, i) => {
            const played = (i / peaks.length) * 100 < pct;
            return (
              <span
                key={i}
                className={`flex-1 rounded-full transition-colors duration-150 ${played ? "bg-gradient-to-t from-blue-400 to-violet-300" : "bg-white/20"}`}
                style={{ height: `${Math.max(6, p * 100)}%` }}
              />
            );
          }) : (
            <div className="h-1 w-full rounded-full bg-white/10" />
          )}
        </div>
        <div className="mt-2 flex justify-between font-mono text-[11px] tabular-nums text-white/50">
          <span data-testid="creator-player-pos">{fmt(shown)}</span>
          <span data-testid="creator-player-dur">{fmt(dur)}</span>
        </div>
      </div>
    </div>
  );
}
